import { useMemo, useState } from 'react'
import { useParams, Navigate, Link, useNavigate, Outlet } from 'react-router-dom'
import { ArrowLeft, Copy } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AgentSectionsNav } from '@/components/projects'
import { useAuth, useAgent, useCreateAgentVersion, useDuplicateAgent, useProject, useUpdateAgent } from '@/hooks'
import type { AgentDetailContext } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { AGENT_YAML_MAX, listUnknownAgentYamlKeys, parseAgentYaml } from '@/lib/agentYaml'
import { downloadBlob } from '@/lib/downloadBlob'
import { Permissions } from '@/lib/permissions'

const NEUTRAL_BADGE_BORDER = 'rgba(118,124,150,1)'
import { toast } from 'sonner'

export function AgentDetailPage() {
  const { agentId } = useParams()
  return <AgentDetailContent key={agentId} />
}

function AgentDetailContent() {
  const { projectId, agentId } = useParams()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const { data: agent, isLoading, isError, isFetching } = useAgent(projectId!, agentId)
  const { data: project } = useProject(projectId)
  const createVersion = useCreateAgentVersion(projectId!)
  const updateAgent = useUpdateAgent(projectId!)
  const duplicateAgent = useDuplicateAgent(projectId!)
  const navigate = useNavigate()

  const [yamlDraft, setYamlDraft] = useState<string | null>(null)
  const [displayNameDraft, setDisplayNameDraft] = useState<string | null>(null)
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null)

  const editorYaml = yamlDraft ?? agent?.yamlContent ?? ''
  const displayName = displayNameDraft ?? agent?.displayName ?? ''
  const description = descriptionDraft ?? agent?.description ?? ''

  const parsedConfig = useMemo(() => {
    try {
      return parseAgentYaml(editorYaml)
    } catch {
      return null
    }
  }, [editorYaml])

  const unknownYamlKeys = useMemo(
    () => (parsedConfig ? listUnknownAgentYamlKeys(editorYaml) : []),
    [parsedConfig, editorYaml]
  )

  if (authLoading) return null

  if (!hasPermission(Permissions.Project.Agent.Read, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  if (isLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground" role="status" aria-live="polite" data-testid="agent-detail-loading">
        Loading agent…
      </div>
    )
  }

  if (isError || !agent) {
    return (
      <div className="space-y-4 py-12 text-center" role="alert" data-testid="agent-detail-error">
        <p className="text-sm text-destructive">Couldn't load this agent. It may have been deleted.</p>
        <Button variant="outline" asChild>
          <Link to={`/projects/${projectId}/workflows/agents`}>
            <ArrowLeft className="h-4 w-4" />
            Back to Agents
          </Link>
        </Button>
      </div>
    )
  }

  const canEdit = !agent.isReadOnly && hasPermission(Permissions.Project.Agent.Update, projectId)
  const canReadMemory = hasPermission(Permissions.Project.Agent.Update, projectId)
  const canManageMemory = agent.memoryEnabled && canReadMemory
  const canDuplicate = hasPermission(Permissions.Project.Agent.Create, projectId)

  const yamlChanged = editorYaml !== agent.yamlContent
  const trimmedDisplayName = displayName.trim()
  const trimmedDescription = description.trim()
  const metadataChanged =
    trimmedDisplayName !== agent.displayName ||
    trimmedDescription !== (agent.description ?? '')
  const isDirty = yamlChanged || metadataChanged
  const yamlTooLong = editorYaml.length > AGENT_YAML_MAX

  const missingRequiredFields = parsedConfig
    ? [
        parsedConfig.systemPrompt.trim() ? null : 'system prompt',
        parsedConfig.model.trim() ? null : 'model',
      ].filter((field): field is string => field !== null)
    : []


  let saveBlockedReason: string | null = null
  if (!parsedConfig) saveBlockedReason = "The YAML is invalid, so it can't be saved. Fix the syntax in the YAML tab."
  else if (yamlTooLong) saveBlockedReason = `The YAML must not exceed ${AGENT_YAML_MAX} characters (currently ${editorYaml.length}).`
  else if (missingRequiredFields.length > 0) saveBlockedReason = `Required before saving: ${missingRequiredFields.join(' and ')}.`
  else if (!displayName.trim()) saveBlockedReason = 'Display name is required.'
  else if (!isDirty) saveBlockedReason = 'No changes to save.'

  const isSaving = createVersion.isPending || updateAgent.isPending

  const handleSave = async () => {
    let metadataSaved = false
    try {
      if (metadataChanged) {
        await updateAgent.mutateAsync({
          id: agent.id,
          data: {
            displayName: trimmedDisplayName,
            ...(trimmedDescription ? { description: trimmedDescription } : {}),
          },
        })
        metadataSaved = true
        setDisplayNameDraft(trimmedDisplayName)
        setDescriptionDraft(trimmedDescription)
      }
      if (yamlChanged) {
        const response = await createVersion.mutateAsync({ id: agent.id, data: { yaml: editorYaml } })
        toast.success(`Saved "${trimmedDisplayName}" as version ${response.version} — workflows pin a version, so the ones already running keep theirs.`)
      } else {
        toast.success(`Saved "${trimmedDisplayName}"`)
      }
    } catch (error) {
      const reason = extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to save agent'
      toast.error(metadataSaved ? `Name and description were saved, but the configuration wasn't: ${reason}` : reason)
    }
  }

  const handleExport = () => {
    downloadBlob(new Blob([editorYaml], { type: 'application/yaml' }), `${agent.name}.yaml`)
  }

  const handleDuplicate = async () => {
    try {
      const result = await duplicateAgent.mutateAsync(agent.id)
      toast.success(`Duplicated "${agent.displayName}"`)
      navigate(`/projects/${projectId}/workflows/agents/${(result as { agentId: string }).agentId}/config`)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to duplicate agent')
    }
  }

  const duplicateBlockedReason = isDirty ? 'Save or discard your changes before duplicating.' : null

  const context: AgentDetailContext = {
    projectId: projectId!,
    agent,
    canEdit,
    canManageMemory,
    canReadMemory,
    editorYaml,
    onYamlChange: setYamlDraft,
    parsedConfig,
    unknownYamlKeys,
    displayName,
    onDisplayNameChange: setDisplayNameDraft,
    description,
    onDescriptionChange: setDescriptionDraft,
    missingRequiredFields,
    isDirty,
    isSaving,
    isRefreshing: isFetching,
    saveBlockedReason,
    onSave: () => void handleSave(),
    onExport: handleExport,
  }

  return (
    <div className="space-y-6" data-testid="agent-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-primary">
            Project: {project?.name ?? 'Loading...'}
          </p>
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="icon" asChild aria-label="Back to Agents">
              <Link to={`/projects/${projectId}/workflows/agents`}>
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <h1 className="truncate text-2xl font-bold text-foreground">{agent.displayName}</h1>
            {agent.isGlobal ? (
              <>
                <Badge variant="outline" className="text-xs" style={{ borderColor: NEUTRAL_BADGE_BORDER }} data-testid="agent-category-chip">System</Badge>
                <Badge variant="outline" className="text-xs text-muted-foreground" style={{ borderColor: NEUTRAL_BADGE_BORDER }} data-testid="agent-readonly-chip">read-only</Badge>
              </>
            ) : (
              <Badge variant="outline" className="text-xs" style={{ borderColor: NEUTRAL_BADGE_BORDER }} data-testid="agent-category-chip">Custom</Badge>
            )}
          </div>
        </div>
        {canDuplicate && (
          <Button
            variant="ghost"
            data-testid="duplicate-agent-button"
            onClick={() => void handleDuplicate()}
            disabled={duplicateBlockedReason !== null || duplicateAgent.isPending}
            title={duplicateBlockedReason ?? undefined}
          >
            <Copy className="h-4 w-4" />
            {duplicateAgent.isPending ? 'Duplicating...' : 'Duplicate'}
          </Button>
        )}
      </div>

      <div className="flex gap-4">
        <AgentSectionsNav showMemory={canReadMemory} />
        <div className="min-w-0 flex-1">
          <Outlet context={context} />
        </div>
      </div>
    </div>
  )
}
